import "./assets/app.css";

import React, { useContext, useEffect, useState } from "react";

import { Router } from "@gatsbyjs/reach-router";
import { CssBaseline } from "@material-ui/core";
import Slide from "@material-ui/core/Slide";
import { createStyles, createTheme, makeStyles, Theme, ThemeProvider } from "@material-ui/core/styles";

import NavigationBar from "./components/navigation-bar";
import { AppContext } from "./contexts/app-context";
import UserAdminPage from "./screens/userAdmin";
import ManageAdminUserPage from "./screens/adminUser";
import ManageApiKeyPage from "./screens/apikey";
import ManageAppSettingPage from "./screens/siteSetting";

import AccountOptoutPage from "./screens/accountOptout";
import EnlistmentPage from "./screens/enlistment";
import PendingEnrollmentCompaniesPage from "./screens/enrollment";
import PendingEnrollmentBatchesPage from "./screens/enrollment/batches";
import PendingEnrollmentBatchItemsPage from "./screens/enrollment/batchItems";
import EnrollmentFilePage from "./screens/enrollmentFileValidation";
import EnrollmentFileReviewPage from "./screens/enrollmentFileValidation/crud/enrollmentFileReview";
import HomePage from "./screens/home";
import JobsPage from "./screens/jobs";
import BatchStatusPage from "./screens/jobs/batchStatus";
import JobRedirectPage from "./screens/jobs/jobRedirect";
import LoginPage from "./screens/login";
import OnboardingPage from "./screens/onboarding";
import ClientClientCRUDPage from "./screens/onboarding/crud/clientcompany-crud";
import KitCRUDPage from "./screens/onboarding/crud/kit-crud";
import ProgramCRUDPage from "./screens/onboarding/crud/program-crud";
import OrderPage from "./screens/manualOrder";
import OrderCRUD from "./screens/manualOrder/crud/manualOrder-crud";
import PageNotFoundPage from "./screens/page-not-found";
import RedirectPage from "./screens/redirectPage";
import ReplacementPage from "./screens/replacement";
import SubscriptionPage from "./screens/subscription";
import ValidationPage from "./screens/validation";
import AppLockedPage from "./screens/appLocked";
import SavesPage from "./screens/saves";
import ContractOrdersPage from "./screens/contractOrder";
import ContractOrderCRUDPage from "./screens/contractOrder/crud/contractorder-curd";

import PreOrdersPage from "./screens/preOrder";

import { SignalWifi0BarIcon } from "./utils/icons";
import { Alert, Box, List, ListItem, ListItemText, Snackbar, Typography } from "./utils/material-ui-core";

interface ICustomRouteProps {
  component: any;
  path: string;
  exact: boolean;
}

const PublicRoute = ({ component: Component, ...rest }: ICustomRouteProps) => <Component {...rest} />;

const App = () => {
  const pageStyles = LocalPageStyles();

  const { isUserAuthenticated, themeType, setThemeType, appReady, snackbarAlertOpen, snackbarAlertProps, hideAlert, appSetting } = useContext(AppContext);

  const [theme, setTheme] = useState(createTheme({ palette: { type: "light" } }));

  const handleSnackbarAlertClose = (event?: React.SyntheticEvent) => {
    hideAlert();
  };

  function disableZoom() {
    document.addEventListener('wheel', preventZoom);
    document.addEventListener('keydown', preventZoom);
  }

  function preventZoom(event) {
    try {
      if (event.ctrlKey === true && event.type === "wheel") {
        //event = null;
        // event.stopPropagation();
        // event.preventDefault();
      }
      else if (event.ctrlKey === true && (event.key === '+' || event.key === '-')) {
        event.preventDefault();
      }
    }
    catch { }

    return false;
  }

  useEffect(() => {
    try {
      disableZoom();
      return () => {
        document.removeEventListener('wheel', preventZoom);
        document.removeEventListener('keydown', preventZoom);
      };
    }
    catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const htmlTagProps = {
      MuiTextField: {
        fullWidth: true,
        InputLabelProps: {
          shrink: true,
        },
      },
      MuiButton: {
        disableElevation: false,
      },
    };

    const typography = {
      fontFamily: ["roboto", "sans-serif"].join(","),
      fontSize: 16,
      fontStyle: 'normal',
      fontWeight: 500
    };

    if (themeType === "dark") {
      setTheme(
        createTheme({
          palette: {
            type: "dark",
            background: {
              default: "#121212",
              paper: "#1f1f1f",
            },
            primary: {
              light: "#61abcb",
              main: "#3a97be",
              dark: "#286985",
              contrastText: "#ffffff",
            },
            secondary: {
              light: "#fbe7a8",
              main: "#FBE193",
              dark: "#af9d66",
              contrastText: "#000000",
            },
            error: {
              light: "#ffa3a9",
              main: "#ff8d94",
              dark: "#b26267",
              contrastText: "#ffffff",
            },
          },
          typography: typography,
          props: htmlTagProps,
          overrides: {
            MuiCssBaseline: {
              "@global": {
                body: {
                  userSelect: "none",
                  scrollbarColor: "#6b6b6b",
                  "&::-webkit-scrollbar": {
                    width: "15px",
                  },
                  [theme.breakpoints.down("xs")]: {
                    "& .MuiDialog-paperFullWidth": {
                      width: '100% !important',
                      marginLeft: "5px !important",
                      marginRight: "5px !important",
                    }
                  },

                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderRadius: "10px",
                    borderWidth: "5px",
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "white",
                  },
                  "& ::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "& ::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "& ::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderRadius: "10px",
                    borderWidth: "5px",
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "white",
                  },
                },
                "& .MuiGridPanel-root": {
                  border: '1px #999 solid',
                  borderTopWidth: 0,
                  borderBottomWidth: 3,
                },
                ".MuiDialogContent-root": {
                  "&::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "white",
                  },
                  "& ::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "& ::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "& ::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "white",
                  },
                },
                ".MuiTableContainer-root": {
                  "&::-webkit-scrollbar": {
                    width: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "white",
                  },
                  "& ::-webkit-scrollbar": {
                    width: "10px",
                  },
                  "& ::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "& ::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "white",
                  },
                },
              },
            },
          },
        })
      );
    } else {
      setTheme(
        createTheme({
          palette: {
            type: "light",
            background: {
              default: "#ffffff",
            },
            primary: {
              light: "#ff935b",
              main: "#ff7832",
              dark: "#b25423",
              contrastText: "#ffffff",
            },
            secondary: {
              light: "#3a97be",
              main: "#097DAE",
              dark: "#065779",
              contrastText: "#ffffff",
            },
            error: {
              light: "#ffa3a9",
              main: "#ff8d94",
              dark: "#b26267",
              contrastText: "#ffffff",
            },
          },
          typography: typography,
          props: htmlTagProps,
          overrides: {
            MuiCssBaseline: {
              "@global": {
                body: {
                  userSelect: "none",
                  "&::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderRadius: "5px",
                    borderWidth: "2px",
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "#9E9E9E",
                  },
                  "& ::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "& ::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "& ::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderRadius: "5px",
                    borderWidth: "2px",
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "#9E9E9E",
                  },
                },
                ".MuiDialogContent-root": {
                  "&::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "#9E9E9E",
                  },

                  "& ::-webkit-scrollbar": {
                    width: "15px",
                  },
                  "&: :-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "& ::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "#9E9E9E",
                  },
                },
                ".MuiTableContainer-root": {
                  "&::-webkit-scrollbar": {
                    width: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "#9E9E9E",
                  },
                  "& ::-webkit-scrollbar": {
                    width: "10px",
                  },
                  "& ::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "& ::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                    borderStyle: "solid",
                    borderColor: "transparent",
                    backgroundClip: "content-box",
                    backgroundColor: "#9E9E9E",
                  },
                },
                ".MuiDialog-container":{
                  animation: 'slideFromDown 2s forwards',
                }
              },
            },
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeType]);

  useEffect(() => {
    const tempThemeType = localStorage.getItem("ThemeType");
    if (tempThemeType === "dark" || tempThemeType === "light") {
      if (themeType !== tempThemeType) {
        setThemeType(tempThemeType);
      }
    }
  }, [setThemeType, themeType]);

  return (

    <Box p={0} className={pageStyles.appRootContainer}>
      {appSetting.online ? (
        <>

          {/* Setting Global Theme */}

          <ThemeProvider theme={theme}>
            <CssBaseline />

            {/* App Global Snackbar (Toast) */}

            <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={snackbarAlertOpen} TransitionComponent={Slide} autoHideDuration={snackbarAlertProps.severity === "error" ? 10000 : 5000} onClose={handleSnackbarAlertClose}>
              <Alert onClose={handleSnackbarAlertClose} elevation={6} variant="filled" severity={snackbarAlertProps.severity as AlertSeverityColor}>
                <Typography variant="body1">{snackbarAlertProps.message}</Typography>
              </Alert>
            </Snackbar>

            {/* App Global Navigation Bar */}

            {isUserAuthenticated && appReady ? <NavigationBar /> : null}

            {/* App Routing Paths */}

            <Router>
              {isUserAuthenticated && appReady ? (
                <>
                  <PublicRoute component={HomePage} path="/" exact={true} />
                  <PublicRoute component={RedirectPage} path="/redirectPage/:routeparamRedirectPageUrl" exact={true} />
                  <PublicRoute component={HomePage} path="/home" exact={true} />
                  <PublicRoute component={AppLockedPage} path="/locked" exact={true} />
                  <PublicRoute component={OnboardingPage} path="/onboarding" exact={true} />
                  <PublicRoute component={OnboardingPage} path="/onboarding/:pathName" exact={true} />
                  <PublicRoute component={ClientClientCRUDPage} path="/onboarding/clientcompanies/:clientCompanyKey" exact={true} />
                  <PublicRoute component={ProgramCRUDPage} path="/onboarding/programs/:programKey" exact={true} />
                  <PublicRoute component={ProgramCRUDPage} path="/onboarding/programs/:programKey/:clientCompanyKey" exact={true} />
                  <PublicRoute component={KitCRUDPage} path="/onboarding/kits/:kitKey" exact={true} />
                  <PublicRoute component={KitCRUDPage} path="/onboarding/kits/:kitKey/kititems/:kitItemKey" exact={true} />
                  <PublicRoute component={JobRedirectPage} path="/jobs/:routeParamJobTitle" exact={true} />
                  <PublicRoute component={JobsPage} path="/jobs" exact={true} />
                  <PublicRoute component={ManageAdminUserPage} path="/users" exact={true} />
                  <PublicRoute component={ManageAdminUserPage} path="/managesettings/users" exact={true} />
                  <PublicRoute component={UserAdminPage} path="/managesettings/useradmin" exact={true} />
                  <PublicRoute component={UserAdminPage} path="/managesettings" exact={true} />
                  <PublicRoute component={ManageApiKeyPage} path="/managesettings/apikeys" exact={true} />
                  <PublicRoute component={ManageAppSettingPage} path="/managesettings/site" exact={true} />
                  <PublicRoute component={SubscriptionPage} path="/subscription" exact={true} />
                  <PublicRoute component={ValidationPage} path="/validation" exact={true} />
                  <PublicRoute component={OrderCRUD} path="/manualorders/:orderKey" exact={true} />
                  <PublicRoute component={OrderPage} path="/manualorders" exact={true} />
                  <PublicRoute component={PageNotFoundPage} path="*" exact={true} />
                  <PublicRoute component={ReplacementPage} path="/replacement" exact={true} />
                  <PublicRoute component={EnrollmentFilePage} path="/enrollment/files" exact={true} />
                  <PublicRoute component={EnrollmentFilePage} path="/enrollment/files/:uploadDialog" exact={true} />
                  <PublicRoute component={EnrollmentFileReviewPage} path="/enrollment/files/:preBatchLoadKey/review" exact={true} />
                  <PublicRoute component={BatchStatusPage} path="/batch/status" exact={true} />
                  <PublicRoute component={PendingEnrollmentCompaniesPage} path="/enrollmentcorrection" exact={true} />
                  <PublicRoute component={PendingEnrollmentCompaniesPage} path="/enrollmentcorrection/companies" exact={true} />
                  <PublicRoute component={PendingEnrollmentBatchesPage} path="/enrollmentcorrection/companies/:companyKey/batches" exact={true} />
                  <PublicRoute component={PendingEnrollmentBatchItemsPage} path="/enrollmentcorrection/companies/:companyKey/batches/:batchLoadKey/:batchLoadId" exact={true} />
                  <PublicRoute component={EnlistmentPage} path="/enlistmentreviews" exact={true} />
                  <PublicRoute component={SavesPage} path="/saves" exact={true} />
                  <PublicRoute component={ContractOrderCRUDPage} path="/contractorders/:contractOrderKey" exact={true} />
                  <PublicRoute component={ContractOrdersPage} path="/contractorders" exact={true} />
                  <PublicRoute component={PreOrdersPage} path="/preordercorrections/:preOrderKey" exact={true} />
                  <PublicRoute component={PreOrdersPage} path="/preordercorrections" exact={true} />
                  <PublicRoute component={AccountOptoutPage} path="/accountoptout" exact={true} />
                </>
              ) : (
                <>
                  <PublicRoute component={LoginPage} path="/" exact={true} />
                  <PublicRoute component={LoginPage} path="/login" exact={true} />
                  <PublicRoute component={PageNotFoundPage} path="*" exact={false} />
                </>
              )}
            </Router>
          </ThemeProvider>
        </>
      ) : (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" style={{ height: "100vh" }}>
          <Box p={3}>
            <SignalWifi0BarIcon style={{ fontSize: 60, color: "#000000" }} />
          </Box>
          <Box component="span">
            <Box p={0}>
              <Typography variant="h5">You are offline.</Typography>
            </Box>
            <Box p={3}>
              <Typography variant="h6">Try:</Typography>

              <List>
                <ListItem>
                  <ListItemText primary="Turning off aeroplane mode" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Turning on mobile data or Wi-Fi" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Checking the signal in your area" />
                </ListItem>
              </List>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    appRootContainer: {
      flexGrow: 1,
      maxWidth: "1870px",
      margin: 'auto',
      zoom: 1
    },
  })
);

export default App;
